import { useEffect } from 'react'
import { envConfig } from '@/lib/env-config'

interface DocumentHeadOptions {
  title?: string
  description?: string
  favicon?: boolean
  openGraph?: {
    title?: string
    description?: string
    image?: string
  }
}

export function useDocumentHead(options: DocumentHeadOptions = {}) {
  useEffect(() => {
    const { title, description, favicon = false, openGraph } = options

    // Set page title
    if (title) {
      document.title = `${title} | Super AI`
    }

    // Set meta description
    if (description) {
      let metaDescription = document.querySelector(
        'meta[name="description"]',
      ) as HTMLMetaElement
      if (!metaDescription) {
        metaDescription = document.createElement('meta')
        metaDescription.name = 'description'
        document.head.appendChild(metaDescription)
      }
      metaDescription.content = description
    }

    // Set Open Graph meta tags
    if (openGraph) {
      const setOgMeta = (property: string, content: string) => {
        let ogMeta = document.querySelector(
          `meta[property="${property}"]`,
        ) as HTMLMetaElement
        if (!ogMeta) {
          ogMeta = document.createElement('meta')
          ogMeta.setAttribute('property', property)
          document.head.appendChild(ogMeta)
        }
        ogMeta.content = content
      }

      if (openGraph.title) setOgMeta('og:title', openGraph.title)
      if (openGraph.description)
        setOgMeta('og:description', openGraph.description)
      if (openGraph.image) setOgMeta('og:image', openGraph.image)
    }

    // Set environment-specific favicon
    if (favicon) {
      const existingFavicons = document.querySelectorAll('link[rel*="icon"]')
      existingFavicons.forEach(link => {
        if (!link.getAttribute('href')?.includes('safari-pinned-tab')) {
          link.remove()
        }
      })

      const isDark = document.documentElement.classList.contains('dark')
      const faviconIco = isDark
        ? '/favicon__black.ico'
        : `/${envConfig.faviconPrefix}favicon.ico`

      const faviconLinks = [
        { rel: 'icon', href: faviconIco },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '16x16',
          href: isDark
            ? '/favicon__black.png'
            : `/${envConfig.faviconPrefix}favicon-16x16.png`,
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '32x32',
          href: isDark
            ? '/favicon__black.png'
            : `/${envConfig.faviconPrefix}favicon-32x32.png`,
        },
        {
          rel: 'apple-touch-icon',
          href: `/${envConfig.faviconPrefix}apple-touch-icon.png`,
        },
      ]

      faviconLinks.forEach(linkData => {
        const link = document.createElement('link')
        Object.entries(linkData).forEach(([key, value]) => {
          link.setAttribute(key, value)
        })
        document.head.appendChild(link)
      })
    }

    // Cleanup function to reset title on unmount if needed
    return () => {
      if (title) {
        document.title = 'Super AI - Real Estate Communication Platform'
      }
    }
  }, [options.title, options.description, options.favicon, options.openGraph])
}
